import React from 'react'
import clsx from 'clsx'
import { ArrowRight, ArrowLeft } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'

import { GreyButton } from '../../organisms/ChatWindowV2/Header/EditParticipantsModal'
import BaseModal from '../ChatModals/BaseModal2'
import Button from '../../atoms/Button2'
import MergeContactBox, { User } from './MergeContactBox'
import MediaAvatar from '../../atoms/MediaAvatar'

import { mergeContact } from '@SonarCX/lib/api'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { clearAlerts } from '@SonarCX/slices/chatBoxSlice'
import { selectConvo } from '@SonarCX/slices/convo'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useStyles from './styles'

interface MergeContactModalProps {
  onClose: () => void
  onConfirm?: (user: APIUser) => void
  currentUser?: APIUser
  session?: string
}

const MergeContactModal: React.FC<MergeContactModalProps> = ({
  onClose,
  onConfirm,
  currentUser,
  session
}) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const { conflicts } = useAppSelector(selectConvo)

  const ownerContext = useOwner()
  const queueContext = useQueueState()
  const {
    api: { mergeTimeline }
  } = useSocket()
  const { isSmall: mobile } = useBreakpoint()

  const [selected, setSelected] = React.useState<User>(null)
  const [merging, setMerging] = React.useState(false)
  const sessionId = session ?? queueContext.conversation?.session
  const classes = useStyles()
  const owner = currentUser ?? ownerContext.owner

  // Set selected from conflicts
  React.useEffect(() => {
    if (!owner?.id) {
      return
    }

    if (!(owner.id in conflicts)) {
      return
    }

    const conflicting = conflicts[owner.id]

    if (conflicting.length > 0) {
      const firstConflict = conflicting[0]

      setSelected({
        ...firstConflict.user,
        channels: firstConflict.channels
      })
    }
  }, [conflicts, owner.id])

  const closeMergeModal = React.useCallback(() => {
    setMerging(false)
  }, [])

  const performMerge = React.useCallback(async () => {
    try {
      // Merge owner into selected
      const destUser = selected
      const sourceUser = owner
      const verifiedUserInfo = await mergeContact(destUser.id, sourceUser.id)

      // Update session and timeline locally
      if (sessionId) {
        await mergeTimeline(sessionId, verifiedUserInfo, {
          from: sourceUser,
          to: destUser
        })
        dispatch(clearAlerts())
      }
      closeMergeModal()
      onClose()
      if (onConfirm) {
        onConfirm(verifiedUserInfo)
      }
    } catch (err) {
      dispatch(AddErrorAlert(err))
    }
  }, [
    owner,
    selected,
    closeMergeModal,
    onClose,
    dispatch,
    sessionId,
    mergeTimeline,
    onConfirm
  ])

  const mainModalPaperProps = React.useMemo(() => {
    const common = {
      boxShadow: '0px 20px 30px rgba(0, 0, 0, 0.16)',
      padding: '0'
    }

    if (mobile) {
      return {
        style: {
          ...common,
          top: '0',
          transform: 'translateX(-50%)',
          borderRadius: '0',
          width: '100vw',
          maxWidth: 'unset',
          minHeight: 'unset',
          height: 'calc(100svh - 62.5px)',
          maxHeight: 'unset'
        }
      }
    }

    return {
      style: {
        ...common,
        borderRadius: '7px',
        width: '824px',
        maxWidth: 'unset'
      }
    }
  }, [mobile])

  const submodalPaperProps = React.useMemo(() => {
    const common = {
      border: '1px solid #C6C6C6',
      padding: '0'
    }

    if (mobile) {
      return {
        style: {
          ...common,
          borderRadius: '7px',
          boxSizing: 'border-box',
          boxShadow: '0px 20px 30px rgba(0, 0, 0, 0.15)',
          width: '100%',
          maxWidth: 'calc(100% - 32px)',
          minHeight: 'unset',
          height: 'auto',
          maxHeight: 'unset'
        }
      }
    }

    return {
      style: {
        ...common,

        borderRadius: '8px',
        boxShadow: '0px 20px 30px 2px rgba(0, 0, 0, 0.16)',
        width: '512px',
        maxWidth: 'unset'
      }
    }
  }, [mobile])

  return (
    <>
      <BaseModal
        open
        hideTitle={mobile}
        showX={!mobile}
        title='Merge Contact'
        onClose={onClose}
        PaperProps2={mainModalPaperProps}
        classes={{ title: classes.modalTitle, close: classes.modalClose }}
        childrenClass={mobile && classes.mobileChildren}
      >
        <div className={clsx(classes.root, mobile && classes.mobileRoot)}>
          {mobile && (
            <div className={classes.mobileTitle}>
              <ArrowLeft
                variant='Linear'
                size={theme.size.xl}
                onClick={onClose}
                className={classes.mobileBackArrow}
              />

              <span>Merge Contact</span>
            </div>
          )}

          <div className={clsx(classes.cards, mobile && classes.mobileCards)}>
            {mobile && (
              <div className={classes.cardTitle}>Contact to Merge</div>
            )}

            <MergeContactBox contact={owner} primary mobile={mobile} />

            {!mobile && <ArrowRight className={classes.arrow} />}

            {mobile && <div className={classes.cardTitle}>Merge to</div>}

            <MergeContactBox
              primaryContact={owner}
              contact={selected}
              onSelect={setSelected}
              mobile={mobile}
            />
          </div>

          <div className={classes.info}>
            <div className={classes.infoTitle}>
              What happens when I merge Contacts?
            </div>

            <ol className={classes.infoPoints}>
              <li>
                Conversations, Tasks and Contact Details belonging to{' '}
                <span className='bold'>{owner?.name}</span> (on the left) will
                be merged into{' '}
                {selected?.name ? (
                  <span className='bold'>{selected.name}</span>
                ) : (
                  'the selected contact'
                )}{' '}
                (on the right).
              </li>

              <li>
                The merging of customers cannot be undone. Please make sure you
                merge two profiles belonging to the same customer.
              </li>
            </ol>
          </div>

          <div
            className={clsx(
              classes.buttonArea,
              mobile && classes.buttonAreaMobile
            )}
          >
            <GreyButton
              onClick={onClose}
              mobile={mobile}
              className={clsx(
                classes.button,
                mobile && classes.fullWidthButton
              )}
            >
              Cancel
            </GreyButton>

            <Button
              onClick={() => setMerging(true)}
              disabled={!Boolean(selected)}
              className={clsx(
                classes.button,
                mobile && classes.fullWidthButton
              )}
            >
              Merge
            </Button>
          </div>
        </div>
      </BaseModal>

      {merging && (
        <BaseModal
          open
          hideTitle
          showX={!mobile}
          onClose={closeMergeModal}
          PaperProps2={submodalPaperProps}
          // @ts-ignore
          classes={{ close: classes.modalClose }}
        >
          <div className={classes.submodalTitle}>Merge Confirmation</div>

          <div className={classes.submodalText}>
            <div>Once merged, this action cannot be undone.</div>
            <div>Are you sure want to merge the following contact?</div>
          </div>

          <div className={classes.submodalUsers}>
            <div className={classes.submodalUser}>
              <MediaAvatar
                user={owner}
                hideCard
                className={classes.submodalAvatar}
              />

              <div className='name'>{owner.name}</div>
            </div>

            <ArrowRight className={classes.submodalArrow} />

            <div className={classes.submodalUser}>
              <MediaAvatar
                user={selected}
                hideCard
                className={classes.submodalAvatar}
              />

              <div className='name'>{selected.name}</div>
            </div>
          </div>

          <div
            className={clsx(
              classes.buttonArea,
              classes.buttonAreaSub,
              mobile && classes.submodalButtonAreaMobile
            )}
          >
            <GreyButton
              onClick={closeMergeModal}
              mobile={mobile}
              className={clsx(
                classes.button,
                classes.submodalButton,
                mobile && classes.submodalButtonMobile
              )}
            >
              {mobile ? 'Cancel' : 'No, Keep Separate'}
            </GreyButton>

            <Button
              onClick={performMerge}
              className={clsx(
                classes.button,
                classes.submodalButton,
                mobile && classes.submodalButtonMobile
              )}
            >
              {mobile ? 'Yes' : 'Yes, Merge'}
            </Button>
          </div>
        </BaseModal>
      )}
    </>
  )
}
export default MergeContactModal
